import React from 'react';
import { Button } from '@mui/material';

import './Login.css';
import config from '../../config';

const PKCE_VERIFIER_KEY = 'launchpad_pkce_verifier';
const OAUTH_STATE_KEY = 'launchpad_oauth_state';
const ignoreError = () => undefined;

const toBase64Url = (buffer) => window.btoa(String.fromCharCode(...new Uint8Array(buffer)))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

const randomString = (length) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return toBase64Url(array).slice(0, length);
};

const createCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return toBase64Url(digest);
};

const normalizeLaunchpadUrl = (value) => String(value || '')
  .trim()
  .replace(/^['"]+|['"]+$/g, '')
  .replace(/\s+/g, '');

const parseRelayStateParams = (authorizeUrl) => {
  const relayState = (authorizeUrl.searchParams.get('RelayState') || '').trim();
  if (!relayState) {
    return null;
  }
  try {
    return new URLSearchParams(decodeURIComponent(relayState));
  } catch {
    return new URLSearchParams(relayState);
  }
};

const writeRelayStateParams = (authorizeUrl, relayParams) => {
  authorizeUrl.searchParams.set('RelayState', relayParams.toString());
};

const getAuthorizeParam = (authorizeUrl, key) => {
  const direct = (authorizeUrl.searchParams.get(key) || '').trim();
  if (direct) {
    return direct;
  }
  const relayParams = parseRelayStateParams(authorizeUrl);
  if (!relayParams) {
    return '';
  }
  return (relayParams.get(key) || '').trim();
};

const setAuthorizeParam = (authorizeUrl, key, value) => {
  const relayParams = parseRelayStateParams(authorizeUrl);
  if (relayParams) {
    relayParams.set(key, value);
    writeRelayStateParams(authorizeUrl, relayParams);
    return;
  }
  authorizeUrl.searchParams.set(key, value);
};

const hasTokenEndpoint = (authorizeUrl) => {
  const explicitTokenUrl = normalizeLaunchpadUrl(config.launchpadTokenURL);
  if (explicitTokenUrl) {
    return true;
  }
  const tokenUrlParam = normalizeLaunchpadUrl(getAuthorizeParam(authorizeUrl, 'token_url'));
  if (tokenUrlParam) {
    return true;
  }
  return /\/oauth2\/authorize\/?$/i.test(authorizeUrl.pathname);
};

const buildAuthorizeUrl = async () => {
  const launchpadURL = normalizeLaunchpadUrl(config.launchpadURL);
  let authorizeUrl;
  try {
    authorizeUrl = new URL(launchpadURL);
  } catch {
    throw new Error('Invalid launchpadURL config');
  }
  const redirectUri = `${window.location.origin}/auth`;
  const verifier = randomString(96);
  const state = randomString(64);
  const challenge = await createCodeChallenge(verifier);
  const existingResponseType = getAuthorizeParam(authorizeUrl, 'response_type');
  const shouldUseImplicit = !hasTokenEndpoint(authorizeUrl) && (!existingResponseType || existingResponseType.toLowerCase() === 'code');
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  try {
    localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    localStorage.setItem(OAUTH_STATE_KEY, state);
  } catch {
    ignoreError();
  }
  if (shouldUseImplicit) {
    setAuthorizeParam(authorizeUrl, 'response_type', 'token');
  }
  setAuthorizeParam(authorizeUrl, 'redirect_uri', redirectUri);
  setAuthorizeParam(authorizeUrl, 'state', state);
  if (!shouldUseImplicit) {
    setAuthorizeParam(authorizeUrl, 'code_challenge_method', 'S256');
    setAuthorizeParam(authorizeUrl, 'code_challenge', challenge);
  }
  return authorizeUrl.toString();
};

const isProductionMode = () => String(config.MODE || config.mode || '').trim().toUpperCase() === 'PRODUCTION';

const LoginPage = () => {
  const allowBypassLogin = !isProductionMode();

  const handleLaunchpadLogin = async () => {
    const url = await buildAuthorizeUrl();
    window.location.assign(url);
  };

  const handleBypassLogin = () => {
    if (!allowBypassLogin) {
      return;
    }
    sessionStorage.clear();
    sessionStorage.setItem('isAuthenticated', 'authenticated');
    sessionStorage.setItem('token', 'bypass');
    sessionStorage.setItem('userRoles', JSON.stringify(['ADMIN']));
    window.location.href = '/#';
  };

  return (
    <div className="login-card flex-col">
      <div className="flex-row">
        <img src="../../jsc.png" alt="hippo" />
      </div>
      <h1>Welcome to Hippo</h1>
      <div className="flex-col">
        <Button
          style={{ margin: '0 15px' }}
          variant="contained"
          onClick={handleLaunchpadLogin}
        >
          Login With Launchpad
        </Button>
        {allowBypassLogin ? (
          <Button
            style={{ margin: '15px' }}
            variant="contained"
            onClick={handleBypassLogin}
          >
            Login without Launchpad (temporary)
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default LoginPage;
